import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MapPin, Calendar, Building, ExternalLink, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api';
import { toast } from 'sonner';

const PropertyCard = ({ property, isSaved, onSaveToggle, showNewBadge = false }) => {
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeader } = useAuth();
  const [saving, setSaving] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, expired: false });

  // Calculate countdown to auction
  useEffect(() => {
    const calculateCountdown = () => {
      const auctionDate = new Date(property.auction_date);
      const now = new Date();
      const diff = auctionDate - now;

      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, expired: true });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setCountdown({ days, hours, minutes, expired: false });
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [property.auction_date]);

  const handleSaveToggle = async (e) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      toast.error('Please login to save properties');
      return;
    }

    setSaving(true);

    try {
      if (isSaved) {
        await api.removeSavedProperty(property.property_id, getAuthHeader());
        toast.success('Property removed from saved');
      } else {
        await api.saveProperty(property.property_id, getAuthHeader());
        toast.success('Property saved!');
      }
      onSaveToggle && onSaveToggle();
    } catch (error) {
      toast.error('Failed to update saved status');
    } finally {
      setSaving(false);
    }
  };

  const handleExternalLink = (e) => {
    e.stopPropagation();
    const url = property.property_url || property.source_url;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getCategoryBadgeColor = (category) => {
    switch (category) {
      case 'Residential':
        return 'bg-blue-100 text-blue-800';
      case 'Commercial':
        return 'bg-purple-100 text-purple-800';
      case 'Land':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const isEndingSoon = countdown.days <= 3 && !countdown.expired;
  const isNew = showNewBadge || (property.created_at && 
    (new Date() - new Date(property.created_at)) < 24 * 60 * 60 * 1000);

  return (
    <div 
      className="property-card cursor-pointer group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100"
      onClick={() => navigate(`/properties/${property.property_id}`)}
      data-testid={`property-card-${property.property_id}`}
    >
      <div className="relative h-48 bg-slate-200 overflow-hidden">
        {property.images && property.images.length > 0 ? (
          <img 
            src={property.images[0]} 
            alt={property.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
            <Building className="h-12 w-12 text-slate-400" />
          </div>
        )}
        
        {/* Top badges row */}
        <div className="absolute top-2 left-2 flex gap-2">
          {isNew && (
            <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">
              NEW
            </span>
          )}
          {isEndingSoon && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
              <Clock className="h-3 w-3" />
              ENDING SOON
            </span>
          )}
        </div>

        {/* Save button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 bg-white/90 hover:bg-white h-8 w-8"
          onClick={handleSaveToggle}
          disabled={saving}
          data-testid={`save-property-btn-${property.property_id}`}
        >
          <Heart 
            className={`h-4 w-4 ${isSaved ? 'fill-red-500 text-red-500' : 'text-slate-600'}`} 
          />
        </Button>

        {/* Category badge */}
        <div className="absolute bottom-2 left-2">
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${getCategoryBadgeColor(property.category)}`}>
            {property.category}
          </span>
        </div>

        {/* Countdown timer */}
        {!countdown.expired && (
          <div className="absolute bottom-2 right-2 bg-slate-900/80 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {countdown.days > 0 ? (
              <span>{countdown.days}d {countdown.hours}h</span>
            ) : (
              <span>{countdown.hours}h {countdown.minutes}m</span>
            )}
          </div>
        )}
      </div>

      <div className="p-4">
        {/* Price - Bold and prominent */}
        <p className="text-2xl font-bold text-[#F5A623] mb-2">
          {property.guide_price && property.guide_price !== 'POA' 
            ? property.guide_price 
            : property.price && property.price > 0 
              ? `£${property.price.toLocaleString()}` 
              : 'POA'}
        </p>

        {/* Title */}
        <h3 className="font-heading font-semibold text-base mb-3 text-slate-900 line-clamp-2 leading-tight">
          {property.title}
        </h3>

        <div className="space-y-1.5">
          {/* Location */}
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <MapPin className="h-4 w-4 flex-shrink-0 text-slate-400" />
            <span className="line-clamp-1">{property.location}</span>
          </div>
          
          {/* Auction date */}
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Calendar className="h-4 w-4 flex-shrink-0 text-slate-400" />
            <span>{formatDate(property.auction_date)}</span>
          </div>

          {/* Auction house */}
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Building className="h-4 w-4 flex-shrink-0 text-slate-400" />
            <span className="line-clamp-1 text-xs">From: {property.auction_house}</span>
          </div>
        </div>

        {/* External link button */}
        {(property.property_url || property.source_url) && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <Button
              variant="outline"
              size="sm"
              className="w-full text-xs h-8 border-slate-200 hover:border-[#F5A623] hover:text-[#F5A623]"
              onClick={handleExternalLink}
              data-testid={`view-original-btn-${property.property_id}`}
            >
              <ExternalLink className="h-3 w-3 mr-1.5" />
              View Full Listing on {property.auction_house?.split(' ')[0] || 'Auctioneer'} →
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyCard;
